// src/utils/api.ts — Typed API helper functions

import axios, { AxiosResponse } from 'axios';
import {
  Product, ProductFilters, ProductsApiResponse,
  SingleProductApiResponse, CategoriesApiResponse,
  ChatMessage, ChatApiResponse, AISearchApiResponse,
  SizeRecommendForm, SizeRecommendApiResponse,
  GenerateDescApiResponse, EnquiryForm, EnquiryApiResponse,
} from '../types';

// Node.js backend (Products/Categories + AI + Enquiry)
// Products/categories are proxied through Node to PHP — the browser never
// calls PHP directly (PHP only accepts server-to-server requests with a
// shared token; see backend-node/src/utils/phpClient.ts).
const nodeApi = axios.create({
  baseURL: process.env.REACT_APP_NODE_URL ?? '/api',
  timeout: 20000,
});

// ── Products (proxied via Node) ───────────────────────────────
export const fetchProducts = async (
  filters: ProductFilters = {}
): Promise<ProductsApiResponse> => {
  const params = new URLSearchParams();
  params.set('sort', filters.sort ?? 'newest');   // ← always send sort
  if (filters.category)  params.set('category',  filters.category);
  if (filters.search)    params.set('search',     filters.search);
  if (filters.min_price) params.set('min_price',  filters.min_price);
  if (filters.max_price) params.set('max_price',  filters.max_price);
  if (filters.in_stock)  params.set('in_stock',   'true');
  if (filters.page)      params.set('page',       String(filters.page));
  if (filters.limit)     params.set('limit',      String(filters.limit));

  const res = await nodeApi.get<ProductsApiResponse>(`/products?${params}`);
  return res.data;
};

export const fetchProduct = async (
  id: number
): Promise<SingleProductApiResponse> => {
  const res: AxiosResponse<SingleProductApiResponse> =
    await nodeApi.get(`/products/${id}`);
  return res.data;
};

export const fetchCategories = async (): Promise<CategoriesApiResponse> => {
  const res: AxiosResponse<CategoriesApiResponse> =
    await nodeApi.get('/categories');
  return res.data;
};

// ── AI (Node.js) ──────────────────────────────────────────────
export const sendChatMessage = async (
  messages:       ChatMessage[],
  productContext?: Partial<Product>
): Promise<ChatApiResponse> => {
  const res: AxiosResponse<ChatApiResponse> =
    await nodeApi.post('/ai/chat', { messages, productContext });
  return res.data;
};

export const aiSearch = async (query: string): Promise<AISearchApiResponse> => {
  const res: AxiosResponse<AISearchApiResponse> =
    await nodeApi.post('/ai/search', { query });
  return res.data;
};

export const sizeRecommend = async (
  form:     SizeRecommendForm,
  product?: Partial<Product>
): Promise<SizeRecommendApiResponse> => {
  const res: AxiosResponse<SizeRecommendApiResponse> =
    await nodeApi.post('/ai/size-recommend', { ...form, product });
  return res.data;
};

export const generateDescription = async (
  productName:   string,
  price:         number,
  existingDesc?: string
): Promise<GenerateDescApiResponse> => {
  const res: AxiosResponse<GenerateDescApiResponse> =
    await nodeApi.post('/ai/generate-description', { productName, price, existingDesc });
  return res.data;
};

// ── Enquiry (Node.js) ─────────────────────────────────────────
export const submitEnquiry = async (
  form:             EnquiryForm,
  product_name?:    string,
  product_asin?:    string,
  recaptchaToken?:  string,
  honeypot?:        string,
  // Date.now() captured when the form first mounted — lets the backend
  // reject submissions that arrive suspiciously fast (bots that fill and
  // submit instantly), a check the honeypot alone can miss.
  formOpenedAt?:    number,
): Promise<EnquiryApiResponse> => {
  const res: AxiosResponse<EnquiryApiResponse> =
    await nodeApi.post('/enquiry', {
      ...form,
      product_name,
      product_asin,
      recaptchaToken,
      website:      honeypot ?? '',
      formElapsedMs: formOpenedAt !== undefined ? Date.now() - formOpenedAt : undefined,
    });
  return res.data;
};
